import { Box, Button, Divider, Paper, TextField, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import React from 'react'

import WhatsappAudio from '@/app/module/common/whatsapp/convo/whatsapp-audio'
import WhatsappButtons from '@/app/module/common/whatsapp/convo/whatsapp-buttons'
import WhatsappDocument from '@/app/module/common/whatsapp/convo/whatsapp-document'
import WhatsappImage from '@/app/module/common/whatsapp/convo/whatsapp-image'
import WhatsappList from '@/app/module/common/whatsapp/convo/whatsapp-list'
import WhatsappLocation from '@/app/module/common/whatsapp/convo/whatsapp-location'
import WhatsappVideo from '@/app/module/common/whatsapp/convo/whatsapp-video'
import { extractWhatsappContent } from '@/app/module/common/whatsapp/whatsapp-content'
import { TGetFileFunction } from '@/app/module/conversations/types'
import { useDownloadFile } from '@/app/module/files/hooks/use-download-file'
import { TWhatsappContent } from '@/app/types'

import { useEnsureFilesUrl } from './convo.utils'

export type ChatMessage = { role: 'user' | 'assistant'; content: TWhatsappContent }

type Props = {
  disabledReason?: 'not-launched' | 'no-session'
  messages: ChatMessage[]
  placeholder?: string
  loading?: boolean
  typing?: boolean
  onSend: (text: string) => void
  onStartTesting: () => void
}

const ChatbotTester: React.FC<Props> = ({
  disabledReason,
  messages,
  placeholder = 'Type a message...',
  loading = false,
  typing = false,
  onSend,
  onStartTesting,
}) => {
  const [input, setInput] = React.useState('')
  const messagesContainerRef = React.useRef<HTMLDivElement | null>(null)
  const isDisabled = !!disabledReason || loading
  const [downloadFile] = useDownloadFile()
  const ensureFilesUrl = useEnsureFilesUrl()

  const getFile = React.useCallback<TGetFileFunction>(
    async (file: string) => {
      const downloaded = await downloadFile(file)
      if (!downloaded) {
        return { filename: file, title: file, url: file }
      }

      return { filename: file, title: file, url: downloaded.url }
    },
    [downloadFile],
  )

  const ListWrapper: React.FC<{ content: any }> = ({ content }) => {
    const [open, setOpen] = React.useState(false)
    return (
      <WhatsappList content={content} listOpen={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)} />
    )
  }

  const renderWhatsapp = (content: TWhatsappContent, idx: number) => {
    const parsed = extractWhatsappContent(ensureFilesUrl(content))
    switch (parsed.type) {
      case 'message':
        return <Typography style={{ whiteSpace: 'pre-line' }}>{parsed.content.message}</Typography>
      case 'image':
        return <WhatsappImage files={parsed.content.files} getFile={getFile} message={parsed.content.message} />
      case 'audio':
        return (
          <WhatsappAudio
            audioUrl={parsed.content.files?.[0]}
            message={parsed.content.message}
            getFile={getFile}
            partId={idx}
          />
        )
      case 'video':
        return (
          <WhatsappVideo videoFile={parsed.content.files?.[0]} message={parsed.content.message} getFile={getFile} />
        )
      case 'document':
        return (
          <>
            {parsed.content.files.map((document: string, i: number) => (
              <WhatsappDocument
                key={`${document}-${i}`}
                document={document}
                onClick={() => downloadFile(document, true)}
              />
            ))}
            <Typography style={{ whiteSpace: 'pre-line' }}>{parsed.content.message}</Typography>
          </>
        )
      case 'buttons':
        return <WhatsappButtons content={parsed.content} />
      case 'list':
        return <ListWrapper content={parsed.content} />
      case 'location':
        return <WhatsappLocation location={parsed.content} />
      default:
        return null
    }
  }

  const send = React.useCallback(() => {
    const trimmed = input.trim()
    if (!trimmed) return
    onSend(trimmed)
    setInput('')
  }, [input, onSend])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: Auto scroll to bottom on new messages
  React.useEffect(() => {
    const el = messagesContainerRef.current
    if (el) {
      // smooth scroll only if not first load
      const behavior = el.scrollTop === 0 ? 'auto' : 'smooth'
      el.scrollTo({ top: el.scrollHeight, behavior })
    }
  }, [messages, typing])

  return (
    <Container variant="outlined">
      <SectionTitle variant="subtitle2">Conversation</SectionTitle>
      <Divider />
      <MessagesContainer ref={messagesContainerRef} data-testid="chatbot-conversation">
        {messages.map((m, idx) => (
          <MessageRow key={idx} isUser={m.role === 'user'}>
            <Bubble isUser={m.role === 'user'}>
              <BubbleLabel variant="caption" isUser={m.role === 'user'}>
                {m.role === 'assistant' ? 'Chatbot' : 'You'}
              </BubbleLabel>
              {renderWhatsapp(m.content, idx)}
            </Bubble>
          </MessageRow>
        ))}
        {typing && (
          <TypingLineContainer data-testid="chatbot-typing" aria-label="Chatbot is typing">
            Chatbot is typing
            <InlineTypingDots>
              <span />
              <span />
              <span />
            </InlineTypingDots>
          </TypingLineContainer>
        )}
      </MessagesContainer>
      <Form
        onSubmit={(e) => {
          e.preventDefault()
          send()
        }}
      >
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          fullWidth
          size="small"
          disabled={isDisabled}
        />
        <Button variant="contained" disabled={isDisabled || !input.trim()} onClick={send}>
          {loading ? '...' : 'Send'}
        </Button>
      </Form>
      {disabledReason && (
        <Overlay>
          {disabledReason === 'not-launched' && (
            <Typography variant="body2" color="text.secondary">
              Save the campaign first to enable testing.
            </Typography>
          )}
          {disabledReason === 'no-session' && (
            <>
              <Typography variant="body2" color="text.secondary">
                No active test session yet.
              </Typography>
              <Button color="primary" loading={loading} variant="contained" onClick={() => onStartTesting()}>
                Start testing
              </Button>
            </>
          )}
        </Overlay>
      )}
    </Container>
  )
}

const Container = styled(Paper)(({ theme }) => ({
  p: 2,
  padding: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  minHeight: 0,
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}))

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: '1 1 0%',
  minHeight: 0,
  overflowY: 'auto',
  paddingRight: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(2),
}))

const MessageRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ isUser }) => ({
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',
}))

const Bubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  maxWidth: '75%',
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: isUser ? theme.palette.primary.main : theme.palette.grey[200],
  color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
  boxShadow: theme.shadows[1],
  whiteSpace: 'pre-wrap',
  fontFamily: 'inherit',
  position: 'relative',
}))

const BubbleLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  opacity: 0.7,
  display: 'block',
  marginBottom: theme.spacing(0.25),
  color: isUser ? theme.palette.primary.contrastText : theme.palette.text.secondary,
}))

const Form = styled('form')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginTop: theme.spacing(0),
}))

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  backgroundColor: 'rgba(255,255,255,0.85)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: theme.spacing(3),
  gap: theme.spacing(2),
}))

const TypingAnim = {
  '& span': {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
    opacity: 0.35,
    animation: 'esChatbotBlink 1.2s infinite ease-in-out both',
  },
  '& span:nth-of-type(1)': { animationDelay: '0s' },
  '& span:nth-of-type(2)': { animationDelay: '0.2s' },
  '& span:nth-of-type(3)': { animationDelay: '0.4s' },
  '@keyframes esChatbotBlink': {
    '0%, 80%, 100%': { transform: 'scale(0)', opacity: 0.25 },
    '40%': { transform: 'scale(1)', opacity: 0.9 },
  },
}

const TypingLineContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontSize: theme.typography.caption.fontSize,
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
  userSelect: 'none',
}))

const InlineTypingDots = styled('div')(() => ({
  display: 'inline-flex',
  gap: 4,
  ...TypingAnim,
}))

export default ChatbotTester
